import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AcademyMyPageTabItems, AcademyMyPageLogoutButton } from "./info";
import { Button, Checkbox, Dropdown } from 'components/atoms/index';
import { Dialog, Modal } from 'components/organisms/Modal/Dialog';
import { Icon } from '@/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { academySubscriptionApi } from '@/services/api';
import { formatNumber } from '@/utils/number';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { getSlugPrefix } from '@/utils/slug';
import { message } from 'antd';

type PlanId = 'standard' | 'plus';

const PLAN_BASE_PRICES: Record<PlanId, number> = {
    standard: 29000,
    plus: 40000,
};
const SEAT_PRICE_INCREMENT = 10000;
const DEFAULT_SEAT_RANGE_VALUE = '1-25';
const PLAN_LABELS: Record<PlanId, string> = {
    standard: '스탠다드',
    plus: '플러스',
};
const formatCardDisplay = (method?: string | null, rawMasked?: string | null) => {
    const masked = rawMasked || '';
    const numeric = masked.replace(/\D/g, '');
    const firstFour = numeric.slice(0, 4);
    if (method && firstFour) return `${method} (${firstFour})`;
    if (method) return method;
    if (firstFour) return `**** (${firstFour})`;
    return masked || '-';
};

const getPreviewHeadline = (action?: string) => {
    if (action === 'refund' || action === 'refunded') return '오늘부터 즉시 변경되며, 차액 환불됩니다.';
    if (action === 'charge' || action === 'charged') return '오늘부터 즉시 변경되며, 차액 결제됩니다.';
    return '오늘부터 즉시 변경되며, 차액 정산됩니다.';
};
const PLAN_ITEMS: { id: PlanId; title: string; features: { text: string; included: boolean }[] }[] = [
    {
        id: 'standard',
        title: '스탠다드',
        features: [
            { text: '알파영어 교재 제공', included: true },
            { text: 'AI 채점 기능', included: false },
        ],
    },
    {
        id: 'plus',
        title: '플러스',
        features: [
            { text: '알파영어 교재 제공', included: true },
            { text: 'AI 채점 기능', included: true },
        ],
    },
];

const getPlanLabel = (plan?: string | null) => {
    if (!plan) return '-';
    return PLAN_LABELS[plan as PlanId] || plan;
};

export default function AcademyMypagePlan() {
    const [activeTab, setActiveTab] = useState<'info' | 'plan' | 'paper' | 'payment' | 'class'>('plan');
    const [selectedPlan, setSelectedPlan] = useState<PlanId>('standard');
    const [showPlanChange, setShowPlanChange] = useState(false); // 요금제 변경 화면
    const [showConfirmDialog, setShowConfirmDialog] = useState(false); // 변경 확인 다이얼로그
    const [previewData, setPreviewData] = useState<any>(null); // 미리보기 데이터
    const [isProcessing, setIsProcessing] = useState(false); // 결제 처리 중 상태
    const [showResultModal, setShowResultModal] = useState(false); // 결과 모달 표시
    const [resultStatus, setResultStatus] = useState<'success' | 'fail' | null>(null); // 성공/실패 상태
    const [resultMessage, setResultMessage] = useState(''); // 결과 메시지
    const processedBillingAuthKeyRef = useRef<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { slug: sessionSlug, academyCode: sessionAcademyCode, academyName: sessionAcademyName, status: sessionStatus, role: sessionRole } = useSessionSlug();
    const slugPrefix = sessionSlug ? `/${sessionSlug}` : getSlugPrefix(location.pathname);
    const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
    const isTeacher = (sessionRole || storedRole || '').toUpperCase() === 'TEACHER';
    useEffect(() => {
        if (isTeacher) {
            navigate(`${slugPrefix}/academy/mypage/info`, { replace: true });
        }
    }, [isTeacher, slugPrefix, navigate]);
    // 요금제 현황 데이터 (API 연동)
    const [subscriptionStatus, setSubscriptionStatus] = useState({
        type: 'pending' as 'active' | 'pending' | 'overdue' | 'grace', // 정상 결제중 / 결제 대기 / 결제 미납 / 유예기간
        currentPlan: '-',
        monthlyAmount: '-',
        nextPaymentDate: '-',
        paymentMethod: '-',
        cardNumber: '-',
        userCount: 0,
        studentCount: 0,
        teacherCount: 0,
        paymentHistoryCount: 0,
        seatRange: null as string | null,
        seatRangeLabel: '-',
        canChangePlan: true,
        planChangeBlockedUntil: null as string | null,
        hasBillingKey: false,
    });
    // 사용 인원/약관
    const [seatRange, setSeatRange] = useState<string | null>(null);
    const [usageCount, setUsageCount] = useState<number>(0);
    const [agreedTos, setAgreedTos] = useState<boolean>(false);
    const [agreedPlanRestriction, setAgreedPlanRestriction] = useState<boolean>(false);
    const [showTos, setShowTos] = useState<boolean>(false);
    const canSubmitInitialPayment = agreedTos && !!seatRange;
    // TossPayments - 자동결제(빌링)는 API 클라이언트 키 사용!
    const clientKey = (process.env.REACT_APP_TOSS_API_CLIENT_KEY as string) || '';
    const academyCode = sessionAcademyCode || '';
    const academyUserId = typeof window !== 'undefined' ? (localStorage.getItem('academy_user_id') || '1') : '1';
    const customerKey = useMemo(() => `${academyCode || 'academy'}_${academyUserId}`, [academyCode, academyUserId]);
    const isPaymentChangeBlocked = subscriptionStatus.type === 'overdue';
    const handlePaymentChangeClick = async () => {
        if (isPaymentChangeBlocked) {
            Modal.alert('미납금 완납 후 변경 가능합니다.');
            return;
        }
        await startBillingAuth();
    };

    const seatOptions: { value: string; label: string; min: number; max: number | null }[] = useMemo(() => ([
        { value: '1-25', label: '1 ~ 25명', min: 1, max: 25 },
        { value: '25-50', label: '25 ~ 50명', min: 25, max: 50 },
        { value: '50-75', label: '50 ~ 75명', min: 50, max: 75 },
        { value: '75-100', label: '75 ~ 100명', min: 75, max: 100 },
        { value: '100-125', label: '100 ~ 125명', min: 100, max: 125 },
        { value: '125-150', label: '125 ~ 150명', min: 125, max: 150 },
        { value: '150-175', label: '150 ~ 175명', min: 150, max: 175 },
        { value: '175-200', label: '175 ~ 200명', min: 175, max: 200 },
        { value: '200+', label: '200+명', min: 200, max: null },
    ]), []);

    const getSeatRangeLabel = useCallback((value?: string | null) => {
        if (!value || value === 'null') return '-';
        const matched = seatOptions.find(option => option.value === value);
        if (matched) return matched.label;
        if (value.includes('-')) {
            const [min, max] = value.split('-');
            return `${(min || '').trim()} ~ ${(max || '').trim()}명`;
        }
        if (value.endsWith('+')) {
            return `${value.replace('+', '').trim()}+명`;
        }
        return value;
    }, [seatOptions]);

    const isSeatRangeValid = useCallback((range?: string | null, count: number = 0) => {
        if (!range) return false;
        if (range.endsWith('+')) return true;
        const parts = range.split('-');
        const upper = parts.length > 1 ? parseInt(parts[1], 10) : NaN;
        if (Number.isNaN(upper)) return false;
        return count <= upper;
    }, []);

    const applySubscriptionStatus = useCallback((statusData: any, studentCountValue?: number) => {
        if (!statusData) return;
        const seatRangeFromServer = statusData.seatRange || null;
        const studentCountFromStatus = Number(statusData.studentCount ?? 0);
        const teacherCountFromStatus = Number(statusData.teacherCount ?? 0);
        const cntValueRaw = typeof studentCountValue === 'number'
            ? studentCountValue
            : Number(statusData.userCount ?? (studentCountFromStatus + teacherCountFromStatus));
        const cntValue = Number(cntValueRaw) || (studentCountFromStatus + teacherCountFromStatus) || 0;
        setUsageCount(cntValue);
        setSubscriptionStatus({
            type: (statusData.type || 'pending'),
            currentPlan: statusData.currentPlan || '-',
            monthlyAmount: statusData.monthlyAmount || '-',
            nextPaymentDate: statusData.nextPaymentDate
                ? (String(statusData.nextPaymentDate).split('T')[0].split(' ')[0].slice(0, 10))
                : '-',
            paymentMethod: statusData.paymentMethod || '-',
            cardNumber: statusData.cardNumber || '-',
            userCount: cntValue,
            studentCount: studentCountFromStatus,
            teacherCount: teacherCountFromStatus,
            paymentHistoryCount: Number(statusData.paymentHistoryCount || 0),
            seatRange: seatRangeFromServer,
            seatRangeLabel: getSeatRangeLabel(seatRangeFromServer),
            canChangePlan: statusData.canChangePlan !== false,
            planChangeBlockedUntil: statusData.planChangeBlockedUntil || null,
            hasBillingKey: !!statusData.hasBillingKey,
        });
        if (seatRangeFromServer && isSeatRangeValid(seatRangeFromServer, cntValue)) {
            setSeatRange(seatRangeFromServer);
        }
    }, [getSeatRangeLabel, isSeatRangeValid]);

    const allowedSeatOptions = useMemo(() => {
        if (usageCount <= 0) return seatOptions;
        const foundIndex = seatOptions.findIndex(option => {
            if (option.max == null) return true;
            return usageCount <= option.max;
        });
        const idx = foundIndex >= 0 ? foundIndex : seatOptions.length - 1;
        return seatOptions.slice(idx);
    }, [usageCount, seatOptions]);

    const fallbackSeatRange = useMemo(() => {
        if (seatRange && seatRange !== 'null') return seatRange;
        if (allowedSeatOptions.length > 0) return allowedSeatOptions[0].value;
        if (seatOptions.length > 0) return seatOptions[0].value;
        return DEFAULT_SEAT_RANGE_VALUE;
    }, [seatRange, allowedSeatOptions, seatOptions]);

    const getSeatTierIndex = useCallback((value?: string | null) => {
        if (!value) return 0;
        const idx = seatOptions.findIndex(option => option.value === value);
        return idx >= 0 ? idx : 0;
    }, [seatOptions]);

    const calculatePlanPrice = useCallback((planId: PlanId, value?: string | null) => {
        const base = PLAN_BASE_PRICES[planId] ?? PLAN_BASE_PRICES.standard;
        const seatValue = value && value !== 'null' ? value : fallbackSeatRange;
        const tierIndex = getSeatTierIndex(seatValue);
        return base + (tierIndex * SEAT_PRICE_INCREMENT);
    }, [fallbackSeatRange, getSeatTierIndex]);

    const handleSeatRangeChange = (value: string) => {
        const selectedOption = seatOptions.find(option => option.value === value);
        if (selectedOption && selectedOption.max !== null && usageCount > selectedOption.max) {
            // [QA Fix] 실사용 인원보다 작은 요금 구간 선택 시 즉시 안내
            message.warning('실사용 인원보다 작은 구간은 선택할 수 없습니다.');
            return;
        }
        setSeatRange(value);
    };

    const planChangeBlockedDateLabel = useMemo(() => {
        if (!subscriptionStatus.planChangeBlockedUntil) return null;
        return subscriptionStatus.planChangeBlockedUntil.split('T')[0];
    }, [subscriptionStatus.planChangeBlockedUntil]);

    const cardDisplayLabel = useMemo(
        () => formatCardDisplay(subscriptionStatus.paymentMethod, subscriptionStatus.cardNumber),
        [subscriptionStatus.paymentMethod, subscriptionStatus.cardNumber]
    );

    const formatPlanPrice = (planId: PlanId, value?: string | null) => {
        return `${formatNumber(calculatePlanPrice(planId, value))}원`;
    };

    // 사용인원 자동 선택: 허용 구간의 첫 번째 값으로 프리셋
    useEffect(() => {
        if (!seatRange && allowedSeatOptions.length > 0) {
            setSeatRange(allowedSeatOptions[0].value);
        }
    }, [allowedSeatOptions, seatRange]);

    useEffect(() => {
        if (!showPlanChange) return;
        if (subscriptionStatus.currentPlan === 'standard' || subscriptionStatus.currentPlan === 'plus') {
            setSelectedPlan(subscriptionStatus.currentPlan);
        }
    }, [showPlanChange, subscriptionStatus.currentPlan]);

    useEffect(() => {
        if (sessionStatus !== 'ready' || !academyCode) return;
        const fetchAll = async () => {
            try {
                const [statusRes, countRes] = await Promise.all([
                    academySubscriptionApi.status({ academyCode }),
                    academySubscriptionApi.studentCount({ academyCode }),
                ]);
                const statusData = statusRes?.data || statusRes;
                const cnt = countRes?.data?.count ?? countRes?.count ?? 0;
                applySubscriptionStatus(statusData, Number(cnt) || 0);
            } catch (e) {
                // 실패 시 기본값 유지
            }
        };
        fetchAll();
    }, [academyCode, sessionStatus, applySubscriptionStatus]);

    // 결제(정기 결제용 빌링 인증 시작)
    const startBillingAuth = async () => {
        const TossPayments = (window as any).TossPayments;
        if (!TossPayments || !clientKey) {
            Modal.alert('결제 모듈이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.');
            return;
        }
        const tossPayments = TossPayments(clientKey);
        const payment = tossPayments.payment({ customerKey });
        try {
            await payment.requestBillingAuth({
                method: 'CARD',
                successUrl: `${window.location.origin}/academy/mypage/plan?billingSuccess=1&academyCode=${academyCode}`,
                failUrl: `${window.location.origin}/academy/mypage/plan?billingFail=1`,
                customerName: sessionAcademyName || '학원',
                customerEmail: `${academyCode || 'academy'}@example.com`,
                windowTarget: 'self',
            });
        } catch (e) {
            console.error('[TossBillingAuth] 실패', e);
            const anyE: any = e as any;
            if (anyE?.code === 'NOT_SUPPORTED_METHOD') {
                Modal.alert('자동결제가 활성화되지 않은 키입니다. 테스트/라이브 키 설정을 확인해주세요.');
            } else if (anyE?.message) {
                Modal.alert(`결제창을 열 수 없습니다: ${anyE.message}`);
            } else {
                Modal.alert('결제창을 열 수 없습니다. 잠시 후 다시 시도해주세요.');
            }
        }
    };

    // 빌링 인증 성공 리다이렉트 처리: authKey 수신 → 서버에 billingKey 발급 요청
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const authKey = params.get('authKey');
        const billingSuccess = params.get('billingSuccess');
        const billingFail = params.get('billingFail');

        // 실패 처리
        if (billingFail === '1') {
            const failCode = params.get('code') || params.get('errorCode');
            const failMessage = params.get('message') || params.get('errorMessage') || '';
            const invalidCard =
                /INVALID_CARD_NUMBER|CARD_NUMBER_INVALID/i.test(failCode || '') ||
                /(카드 ?번호|잘못된 카드|유효하지 않은 카드)/i.test(failMessage);
            const alertMessage = invalidCard
                ? '입력한 카드번호가 올바르지 않아 결제수단 변경에 실패했습니다.\n카드번호를 확인한 뒤 다시 시도해주세요.'
                : `결제수단 변경이 취소되었습니다.${failMessage ? `\n${failMessage}` : ''}`;
            Modal.alert(alertMessage);
            setResultStatus(null);
            setResultMessage('');
            setShowResultModal(false);
            // 쿼리스트링 제거
            window.history.replaceState({}, '', window.location.pathname);
            return;
        }

        // 성공 처리
        if (billingSuccess === '1' && authKey) {
            if (processedBillingAuthKeyRef.current === authKey) {
                return;
            }
            processedBillingAuthKeyRef.current = authKey;
            setIsProcessing(true);
            (async () => {
                try {
                    // 1단계: 빌링키 발급
                    await academySubscriptionApi.setBillingKey({ academyCode, authKey, customerKey });
                    
                    // 2단계: 상태 갱신
                    const statusRes = await academySubscriptionApi.status({ academyCode });
                    const statusData = statusRes?.data || statusRes;
                    
                    let updatedStatus = subscriptionStatus.type;
                    let planFromStatus: PlanId | null = null;
                    let seatRangeFromStatus: string | null = null;
                    if (statusData) {
                        updatedStatus = statusData.type || subscriptionStatus.type;
                        if (statusData.currentPlan === 'standard' || statusData.currentPlan === 'plus') {
                            planFromStatus = statusData.currentPlan;
                            setSelectedPlan(statusData.currentPlan);
                        }
                        if (statusData.seatRange && statusData.seatRange !== 'null') {
                            seatRangeFromStatus = statusData.seatRange;
                        }
                        applySubscriptionStatus(statusData);
                    }
                    
                    // 3단계: 상태에 따른 결제 처리
                    if (updatedStatus !== 'active') {
                        // 미결제 상태: 첫 결제 수행
                        const planForCharge: PlanId = planFromStatus ?? selectedPlan;
                        const seatRangeForCharge = seatRangeFromStatus || fallbackSeatRange;
                        const amount = calculatePlanPrice(planForCharge, seatRangeForCharge);
                        await academySubscriptionApi.charge({ 
                            academyCode, 
                            amount, 
                            orderName: '서비스 이용료(정기결제)' 
                        });
                        setResultStatus('success');
                        setResultMessage('결제수단이 등록되고 첫 결제가 완료되었습니다.');
                    } else {
                        // 정상 결제 중: 카드 정보만 변경
                        setResultStatus('success');
                        setResultMessage('결제수단이 성공적으로 변경되었습니다.\n다음 결제일부터 새로운 카드로 결제됩니다.');
                    }
                    
                    setShowResultModal(true);
                } catch (e: any) {
                    console.error('[Billing Process Error]', e);
                    const errorMsg = e?.response?.data?.message || e?.message || '처리 중 오류가 발생했습니다.';
                    setResultStatus('fail');
                    setResultMessage(`결제수단 등록에 실패했습니다.\n${errorMsg}`);
                    setShowResultModal(true);
                } finally {
                    setIsProcessing(false);
                    // 쿼리스트링 제거
                    window.history.replaceState({}, '', window.location.pathname);
                }
            })();
        }
    }, [academyCode, customerKey, selectedPlan, fallbackSeatRange, calculatePlanPrice, subscriptionStatus.type, applySubscriptionStatus]);

    if (isTeacher) {
        return null;
    }

    return (
        <main className='flex flex-col flex-1 relative'>
            {/* 처리 중 로딩 오버레이 */}
            {isProcessing && (
                <div className='fixed inset-0 z-[1200] flex flex-col gap-4 justify-center items-center bg-black bg-opacity-40 backdrop-blur-[1px]'>
                    <div className='flex flex-col gap-4 justify-center items-center p-8 bg-white rounded-2xl shadow-xl'>
                        <div className='w-16 h-16 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin'></div>
                        <div className='text-center'>
                            <p className='text-xl font-bold text-gray-900'>결제 처리 중입니다</p>
                            <p className='mt-2 text-sm text-gray-500'>잠시만 기다려주세요...</p>
                        </div>
                    </div>
                </div>
            )}

            {/* 결과 모달 */}
            <Dialog
                open={showResultModal}
                okText="확인"
                onOk={() => {
                    setShowResultModal(false);
                    if (resultStatus === 'success') {
                        // 성공 시 페이지 새로고침하여 최신 상태 반영
                        window.location.reload();
                    }
                }}
                onCancel={() => setShowResultModal(false)}
                className="max-w-lg"
            >
                <div className='p-6 text-center'>
                    {resultStatus === 'success' ? (
                        <>
                            <div className='flex justify-center mb-4'>
                                <div className='flex justify-center items-center w-16 h-16 bg-green-100 rounded-full'>
                                    <Icon name='check' size={32} className='*:fill-green-600' />
                                </div>
                            </div>
                            <h3 className='mb-3 text-2xl font-bold text-gray-900'>등록 완료</h3>
                            <p className='text-gray-600 whitespace-pre-line'>{resultMessage}</p>
                        </>
                    ) : (
                        <>
                            <div className='flex justify-center mb-4'>
                                <div className='flex justify-center items-center w-16 h-16 bg-red-100 rounded-full'>
                                    <Icon name='close' size={32} className='*:fill-red-600' />
                                </div>
                            </div>
                            <h3 className='mb-3 text-2xl font-bold text-gray-900'>등록 실패</h3>
                            <p className='text-gray-600 whitespace-pre-line'>{resultMessage}</p>
                        </>
                    )}
                </div>
            </Dialog>

            <div className='flex flex-row justify-between items-center pt-8'>
                <h1 className='heading1'>마이페이지</h1>
                <AcademyMyPageLogoutButton />
                {/* 테스트용 상태 변경 버튼들 - 주석 처리 */}
                {/**
                <div className='flex gap-2'>
                    <button 
                        onClick={() => setSubscriptionStatus(prev => ({ ...prev, type: 'active' }))}
                        className='px-3 py-1 text-sm text-green-700 bg-green-100 rounded border'
                    >
                        정상결제
                    </button>
                    <button 
                        onClick={() => setSubscriptionStatus(prev => ({ ...prev, type: 'pending' }))}
                        className='px-3 py-1 text-sm text-orange-700 bg-orange-100 rounded border'
                    >
                        결제대기
                    </button>
                    <button 
                        onClick={() => setSubscriptionStatus(prev => ({ ...prev, type: 'overdue' }))}
                        className='px-3 py-1 text-sm text-red-700 bg-red-100 rounded border'
                    >
                        결제미납
                    </button>
                </div>
                **/}
            </div>
            <div className="mt-5">
                <AcademyMyPageTabItems activeTab={activeTab} setActiveTab={setActiveTab} />
                <div className="py-5">
                    <div className="w-full">
                        {/* 요금제 관리 내용 */}
                        <div className='flex gap-2 mb-4'>
                            <h3 className="text-lg font-semibold">요금제 결제하기</h3>
                            <div className='flex gap-2'>
                                {subscriptionStatus.type === 'overdue' && (
                                        <span className='px-2 py-1 text-sm text-red-500 bg-red-50 rounded border border-red-100'>결제 미납</span>
                                )}
                                {subscriptionStatus.type === 'pending' && (
                                        <span className='px-2 py-1 text-sm text-orange-500 bg-orange-50 rounded border border-orange-100'>결제 대기</span>
                                )}
                                {(subscriptionStatus.type === 'active') && (
                                    <span className='px-2 py-1 text-sm text-green-500 bg-green-50 rounded border border-green-100'>정상 결제 중</span>
                                )}
                                {subscriptionStatus.type === 'grace' && (
                                    <span className='px-2 py-1 text-sm text-yellow-600 bg-yellow-50 rounded border border-yellow-100'>유예기간</span>
                                )}
                            </div>
                        </div>
                        {subscriptionStatus.type === 'overdue' && (
                                  <p className='mb-4 text-sm text-gray-500'>
                                    결제를 이어가시면 서비스 이용 제한 없이 알파영어를 사용하실 수 있습니다.</p>
                                )}
                        {subscriptionStatus.type === 'pending' && (
                            <p className='mb-4 text-sm text-gray-500'>
                                학원 수강생 수에 따라 월 이용 요금이 달라집니다.
                            </p>
                        )}
                        {/* 요금제 현황 - 결제 완료 상태 */}
                        {subscriptionStatus.type === 'active' && !showPlanChange && (
                            <div className='mb-8 bg-white'>
                                <div className='grid grid-cols-4 gap-5  *:p-5  *:rounded-2xl *:bg-gray-50'>
                                    <div className='flex col-span-2 gap-4 justify-between border border-indigo-50'>
                                        <div>
                                            <p className='flex items-center mb-1 text-lg font-bold text-gray-500'>현재 월 결제금액 <span className='pl-2 text-sm font-normal text-gray-500'>{subscriptionStatus.nextPaymentDate} 결제 예정</span></p>
                                            <p className='text-4xl font-bold text-gray-900'>
                                                {formatNumber(subscriptionStatus.monthlyAmount, '-')}
                                                <span className='px-2 py-1 ml-3 text-sm font-normal text-indigo-500 bg-indigo-50 rounded-full'>
                                                    {getPlanLabel(subscriptionStatus.currentPlan)}
                                                </span>
                                            </p>
                                        </div>
                                        <div>
                                            <p className='mb-1 text-sm text-gray-500'>결제 정보</p>
                                            <p className='font-medium text-gray-900'>{cardDisplayLabel}</p>
                                            <p className='text-xs text-gray-500 mt-1'>
                                                다음 결제일부터 변경된 카드로 정기결제가 진행됩니다. {/* [QA Fix] 카드사명 + 앞 4자리 및 안내 */}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className='border border-gray-100'>
                                        <div className='flex gap-2 items-center mb-1'>
                                            <Icon name='person' size={24} className='*:fill-indigo-500' />
                                            <p className='text-lg font-bold text-gray-500'>사용 인원 수</p>
                                        </div>
                                        <p className='text-4xl font-bold text-indigo-500'>
                                            {formatNumber(subscriptionStatus.userCount)}
                                            <span className='pl-1 text-lg text-gray-500'>명</span>
                                        </p>
                                        <p className='text-sm text-gray-500 mt-1'>
                                            요금 구간 {subscriptionStatus.seatRangeLabel || '-'}
                                        </p>
                                    </div>

                                    <div className='border border-gray-100'>
                                        <div className='flex gap-2 items-center mb-1'>
                                            <Icon name='money' size={24} className='*:fill-indigo-500' />
                                            <p className='text-lg font-bold text-gray-500'>요금 납부 이력</p>
                                        </div>
                                        <p className='text-4xl font-bold text-indigo-500'>{formatNumber(subscriptionStatus.paymentHistoryCount)}<span className='pl-1 text-lg text-gray-500'>건</span></p>
                                    </div>
                                </div>
                                <section className='flex gap-3 *:w-full *:max-w-[300px] justify-center my-8'>
                                    <Button 
                                        intent='secondary' 
                                        size='large'
                                        onClick={handlePaymentChangeClick}
                                        disabled={isPaymentChangeBlocked}
                                    >
                                        결제정보 변경하기
                                    </Button>
                                    <Button 
                                        size='large'
                                        onClick={() => {
                                            if (!subscriptionStatus.canChangePlan) {
                                                Modal.alert(planChangeBlockedDateLabel
                                                    ? `요금제는 결제일마다 1회만 변경할 수 있습니다.\n${planChangeBlockedDateLabel} 이후 다시 시도해주세요.`
                                                    : '요금제는 결제일마다 1회만 변경할 수 있습니다. 다음 결제일까지 기다려주세요.');
                                                return;
                                            }
                                            setAgreedPlanRestriction(false);
                                            setAgreedTos(false);
                                            setShowPlanChange(true);
                                        }}
                                    >
                                        요금제 변경하기
                                    </Button>
                                </section>
                                {!subscriptionStatus.canChangePlan && (
                                    <p className='mt-2 text-sm text-center text-red-500'>
                                        {planChangeBlockedDateLabel
                                            ? `요금제 변경은 월 1회만 가능합니다. ${planChangeBlockedDateLabel} 이후에 다시 시도해주세요.`
                                            : '요금제 변경은 월 1회만 가능합니다. 다음 결제일까지 기다려주세요.'}
                                    </p>
                                )}
                                {isPaymentChangeBlocked && (
                                    <p className='mt-2 text-sm text-center text-red-500'>
                                        미납금 완납 후 결제수단을 변경할 수 있습니다. {/* [QA Fix] 미납 시 결제수단 변경 제한 안내 */}
                                    </p>
                                )}
                            </div>
                        )}
                        
                        {/* 요금제 변경 화면 - 결제 완료 상태에서 변경하기 클릭 시 */}
                        {subscriptionStatus.type === 'active' && showPlanChange && (
                            <>
                                <div className='mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100'>
                                    <p className='text-sm text-blue-700'>
                                        <span className='font-medium'>💡 요금제 변경 안내</span>
                                        <br />
                                        • 혜택이 오늘부터 즉시 변경 적용됩니다.
                                        <br />
                                        • 요금제 상향 시: 일할 계산하여 차액이 즉시 결제됩니다.
                                        <br />
                                        • 요금제 하향 시: 일할 계산하여 차액이 환불 처리됩니다.
                                        <br />
                                        • 다음 결제일부터 변경된 요금제로 정기결제가 진행됩니다.
                                    </p>
                                </div>

                                <div className='mb-4 p-4 bg-gray-50 rounded-lg'>
                                    <p className='mb-2 text-sm font-medium text-gray-700'>현재 요금제</p>
                                    <div className='flex gap-4 items-center'>
                                        <p className='text-lg font-semibold text-gray-900'>
                                            {getPlanLabel(subscriptionStatus.currentPlan)}
                                        </p>
                                        <p className='text-2xl font-bold text-indigo-500'>
                                            {formatNumber(subscriptionStatus.monthlyAmount, '-')}
                                        </p>
                                    </div>
                                </div>

                                <div className='flex flex-col gap-1.5 mb-5'>
                                    <p className=''>사용 인원</p>
                                    <Dropdown
                                        options={allowedSeatOptions.map(o => ({ value: o.value, label: o.label }))}
                                        className='w-full max-w-[300px]'
                                        placeholder='사용 인원을 선택해주세요'
                                        onChange={handleSeatRangeChange}
                                        value={seatRange || undefined}
                                    />
                                    <p className='text-sm text-gray-500'>
                                        현재 실사용 인원: {formatNumber(usageCount)}명
                                    </p>
                                </div>

                                <section className='grid grid-cols-2 gap-10 my-5'>
                                    {PLAN_ITEMS.map((plan) => {
                                        const priceLabel = formatPlanPrice(plan.id, seatRange);
                                        return (
                                        <div 
                                            key={plan.id}
                                            className='flex flex-col gap-1.5 p-10 px-8 cursor-pointer' 
                                            style={{
                                                border: selectedPlan === plan.id ? '3px solid transparent' : '3px solid #EEF2FF',
                                                borderRadius: '2rem',
                                                background: selectedPlan === plan.id 
                                                    ? 'linear-gradient(white, white) padding-box, linear-gradient(to right, #6366F1, #CECFFF) border-box'
                                                    : 'white'
                                            }}
                                            onClick={() => setSelectedPlan(plan.id)}
                                        >
                                            <Checkbox 
                                                checked={selectedPlan === plan.id}
                                                onChange={() => setSelectedPlan(plan.id)}
                                                size='large'
                                                showIcon={true}
                                            />
                                            <p className='text-4xl font-bold mt-1.5'>{plan.title}</p>
                                            <div className='p-4 my-8 space-y-2 text-sm text-gray-500 bg-gray-50 rounded-lg'>
                                                {plan.features.map((feature, index) => (
                                                    <p key={index} className='flex gap-2 text-xl'>
                                                        <Icon 
                                                            name='check' 
                                                            size={28} 
                                                            className={`*:fill-${feature.included ? 'indigo-500' : 'gray-500'}`} 
                                                        />
                                                        <span className={feature.included ? '' : 'line-through text-gray-400'}>
                                                            {feature.text}
                                                        </span>
                                                    </p>
                                                ))}
                                            </div>
                                            <strong className='text-5xl font-bold text-indigo-500'>
                                                {priceLabel}<span className='text-xl'>/월</span>
                                            </strong>
                                        </div>
                                        );
                                    })}
                                </section>

                                <section className='flex flex-col gap-2 mt-5'>
                                    <div className='flex gap-px items-center'>
                                        <Checkbox 
                                            checked={agreedPlanRestriction}
                                            onChange={(v:boolean) => setAgreedPlanRestriction(v)}
                                            label='요금제 변경 시, 차월 결제일까지는 다시 변경하실 수 없는 것에 동의합니다.'
                                        />
                                    </div>
                                </section>

                                <section className='flex flex-col gap-2 mt-2'>
                                    <div className='flex gap-px items-center'>
                                        <Checkbox 
                                            checked={agreedTos}
                                            onChange={(v:boolean) => setAgreedTos(v)}
                                            label='서비스 이용약관'
                                        />
                                        <button className='' onClick={() => setShowTos(true)}>
                                            <Icon name='arrowDown' size={20} className='rotate-270 *:fill-gray-400' />
                                        </button>
                                    </div>
                                    {!agreedTos && (
                                        <p className='text-sm text-red-500'>
                                            서비스 이용약관에 동의해야 요금제 변경을 진행할 수 있습니다.
                                        </p>
                                    )}
                                </section>

                                <section className='flex gap-3 *:w-full *:max-w-[300px] justify-center my-8'>
                                    <Button 
                                        intent='secondary' 
                                        size='large'
                                        onClick={() => {
                                            setShowPlanChange(false);
                                            setAgreedPlanRestriction(false);
                                            setAgreedTos(false);
                                        }}
                                    >
                                        취소
                                    </Button>
                                    <Button 
                                        size='large'
                                        onClick={async () => {
                                            if (!agreedPlanRestriction) {
                                                Modal.alert('요금제 변경 제한에 동의해주세요');
                                                return;
                                            }
                                            if (!agreedTos) {
                                                Modal.alert('서비스 이용약관에 동의 해주세요');
                                                return;
                                            }
                                            // 미리보기 API 호출
                                            setIsProcessing(true);
                                            try {
                                                const res = await academySubscriptionApi.previewChangePlan({
                                                    academyCode,
                                                    newPlan: selectedPlan,
                                                    newSeatRange: fallbackSeatRange
                                                });
                                                const data = res?.data || res;
                                                setPreviewData(data);
                                                setShowConfirmDialog(true);
                                            } catch (e: any) {
                                                const errorMsg = e?.response?.data?.message || e?.message || '요금제 변경 미리보기를 불러올 수 없습니다.';
                                                setResultStatus('fail');
                                                setResultMessage(errorMsg);
                                                setShowResultModal(true);
                                            } finally {
                                                setIsProcessing(false);
                                            }
                                        }}
                                    >
                                        요금제 변경하기
                                    </Button>
                                </section>
                            </>
                        )}

                        {/* 요금제 선택 및 결제 - 미결제 상태에서만 표시 */}
                        {(subscriptionStatus.type === 'pending' || subscriptionStatus.type === 'overdue' || subscriptionStatus.type === 'grace') && (
                            <>
                                <div className='flex flex-col gap-1.5 '>
                                    <p className=''>사용 인원</p>
                                    <Dropdown
                                        options={allowedSeatOptions.map(o => ({ value: o.value, label: o.label }))}
                                        className='w-full max-w-[300px]'
                                        placeholder='사용 인원을 선택해주세요'
                                        onChange={handleSeatRangeChange}
                                        value={seatRange || undefined}
                                    />
                                    <p className='text-sm text-gray-500'>
                                        현재 실사용 인원: {formatNumber(usageCount)}명
                                    </p>
                                </div>

                                <section className='grid grid-cols-2 gap-10 my-5'>
                                    {PLAN_ITEMS.map((plan) => {
                                        const priceLabel = formatPlanPrice(plan.id, seatRange);
                                        return (
                                        <div 
                                            key={plan.id}
                                            className='flex flex-col gap-1.5 p-10 px-8 cursor-pointer' 
                                            style={{
                                                border: selectedPlan === plan.id ? '3px solid transparent' : '3px solid #EEF2FF',
                                                borderRadius: '2rem',
                                                background: selectedPlan === plan.id 
                                                    ? 'linear-gradient(white, white) padding-box, linear-gradient(to right, #6366F1, #CECFFF) border-box'
                                                    : 'white'
                                            }}
                                            onClick={() => setSelectedPlan(plan.id)}
                                        >
                                            <Checkbox 
                                                checked={selectedPlan === plan.id}
                                                onChange={() => setSelectedPlan(plan.id)}
                                                size='large'
                                                showIcon={true}
                                            />
                                            <p className='text-4xl font-bold mt-1.5'>{plan.title}</p>
                                            <div className='p-4 my-8 space-y-2 text-sm text-gray-500 bg-gray-50 rounded-lg'>
                                                {plan.features.map((feature, index) => (
                                                    <p key={index} className='flex gap-2 text-xl'>
                                                        <Icon 
                                                            name='check' 
                                                            size={28} 
                                                            className={`*:fill-${feature.included ? 'indigo-500' : 'gray-500'}`} 
                                                        />
                                                        <span className={feature.included ? '' : 'line-through text-gray-400'}>
                                                            {feature.text}
                                                        </span>
                                                    </p>
                                                ))}
                                            </div>
                                            <strong className='text-5xl font-bold text-indigo-500'>
                                                {priceLabel}<span className='text-xl'>/월</span>
                                            </strong>
                                        </div>
                                        );
                                    })}
                                </section>
                                <section className='flex flex-col gap-2'>
                                    <div className='flex gap-px items-center'>
                                        <Checkbox 
                                            checked={agreedTos}
                                            onChange={(v:boolean) => setAgreedTos(v)}
                                            label='서비스 이용약관'
                                        />
                                        <button className='' onClick={() => setShowTos(true)}>
                                            <Icon name='arrowDown' size={20} className='rotate-270 *:fill-gray-400' />
                                        </button>
                                    </div>
                                    {!agreedTos && (
                                        <p className='text-sm text-red-500'>
                                            서비스 이용약관에 동의해야 결제를 진행할 수 있습니다.
                                        </p>
                                    )}
                                </section>

                                <section className='flex gap-3 *:w-full *:max-w-[300px] justify-center my-8'>
                                    {(subscriptionStatus.type === 'grace' && subscriptionStatus.paymentHistoryCount > 0) && (
                                        <Button intent='secondary' size='large' onClick={() => navigate('/academy/mypage/paymentHistory')}>이전 요금 납부이력 확인</Button>
                                    )}
                                    <Button
                                        size='large'
                                        disabled={!canSubmitInitialPayment}
                                        onClick={async () => {
                                            if (!agreedTos) {
                                                Modal.alert('서비스 이용약관에 동의 해주세요');
                                                return;
                                            }
                                            if (!seatRange) {
                                                Modal.alert('사용 인원을 선택해주세요');
                                                return;
                                            }
                                            try {
                                                const res = await academySubscriptionApi.init({ seatRange, plan: selectedPlan, academyCode });
                                                if (!(res?.success ?? true)) {
                                                    throw new Error(res?.message || '초기화 실패');
                                                }
                                                // 이미 결제수단이 등록되어 있다면 바로 결제 시도
                                                if (subscriptionStatus.hasBillingKey) {
                                                    const confirmed = await Modal.confirm(
                                                        <div className="text-center pt-4 whitespace-pre-line">
                                                            이미 등록된 결제수단으로 결제를 진행하시겠습니까?
                                                        </div>,
                                                        '결제하기',
                                                        '결제수단 변경'
                                                    );
                                                    
                                                    if (confirmed) {
                                                        setIsProcessing(true);
                                                        try {
                                                            const amount = calculatePlanPrice(selectedPlan, seatRange);
                                                            await academySubscriptionApi.charge({ 
                                                                academyCode, 
                                                                amount, 
                                                                orderName: '서비스 이용료(정기결제)' 
                                                            });
                                                            setResultStatus('success');
                                                            setResultMessage('결제가 완료되었습니다.');
                                                            setShowResultModal(true);
                                                        } catch (e: any) {
                                                            console.error('[Charge Error]', e);
                                                            const msg = e?.response?.data?.message || e?.message || '결제 처리에 실패했습니다.';
                                                            Modal.alert(msg);
                                                        } finally {
                                                            setIsProcessing(false);
                                                        }
                                                    } else {
                                                        await startBillingAuth();
                                                    }
                                                } else {
                                                    await startBillingAuth();
                                                }
                                            } catch (e: any) {
                                                console.error('[Subscription Init] 실패', e);
                                                const msg = e?.response?.data?.message || e?.message || '잠시 후 다시 시도해주세요.';
                                                Modal.alert(`결제를 시작하지 못했습니다. ${msg}`);
                                            }
                                        }}
                                    >결제 하기</Button>
                                </section>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {/* 요금제 변경 확인 다이얼로그 */}
            <Dialog
                open={showConfirmDialog}
                okText="확인"
                cancelText="취소"
                onOk={async () => {
                    setShowConfirmDialog(false);
                    setIsProcessing(true);
                    try {
                        const res = await academySubscriptionApi.changePlan({
                            academyCode,
                            newPlan: selectedPlan,
                            newSeatRange: fallbackSeatRange
                        });
                        const data = res?.data || res;
                        
                        // 결과 메시지 생성
                        let message = '';
                        if (data.action === 'charged' || data.action === 'charge') {
                            const planNames = { standard: '스탠다드', plus: '플러스' };
                            const currentPlanName = planNames[data.currentPlan as keyof typeof planNames] || data.currentPlan;
                            const newPlanName = planNames[data.newPlan as keyof typeof planNames] || data.newPlan;
                            message = `✅ 요금제가 변경되었습니다\n\n${currentPlanName} → ${newPlanName}\n\n💳 차액 ${formatNumber(data.difference)}원이 결제되었습니다.\n다음 결제일부터 ${newPlanName} 요금(${formatNumber(data.newMonthlyAmount)}원/월)으로 정기결제됩니다.`;
                        } else if (data.action === 'refunded' || data.action === 'refund') {
                            const planNames = { standard: '스탠다드', plus: '플러스' };
                            const currentPlanName = planNames[data.currentPlan as keyof typeof planNames] || data.currentPlan;
                            const newPlanName = planNames[data.newPlan as keyof typeof planNames] || data.newPlan;
                            message = `✅ 요금제가 변경되었습니다\n\n${currentPlanName} → ${newPlanName}\n\n💰 차액 ${formatNumber(data.refundAmount)}원이 PG(토스페이먼츠)를 통해 환불 처리되었습니다.\n다음 결제일부터 ${newPlanName} 요금(${formatNumber(data.newMonthlyAmount)}원/월)으로 정기결제됩니다.`;
                        } else if (data.action === 'no_charge_needed') {
                            message = `✅ 요금제가 변경되었습니다\n\n차액이 없어 추가 결제나 환불이 발생하지 않습니다.`;
                        } else {
                            message = data.message || '요금제가 변경되었습니다.';
                        }
                        
                        setShowPlanChange(false);
                        setAgreedPlanRestriction(false);
                        setAgreedTos(false);
                        try {
                            const latestStatusRes = await academySubscriptionApi.status({ academyCode });
                            const latestStatusData = latestStatusRes?.data || latestStatusRes;
                            applySubscriptionStatus(latestStatusData);
                        } catch (statusRefreshError) {
                            console.error('[Subscription Status Refresh] 실패', statusRefreshError);
                        }
                        setResultStatus('success');
                        setResultMessage(message);
                        setShowResultModal(true);
                    } catch (e: any) {
                        const errorMsg = e?.response?.data?.message || e?.message || '요금제 변경에 실패했습니다.';
                        setShowPlanChange(false);
                        setAgreedPlanRestriction(false);
                        setAgreedTos(false);
                        setResultStatus('fail');
                        setResultMessage(errorMsg);
                        setShowResultModal(true);
                    } finally {
                        setIsProcessing(false);
                    }
                }}
                onCancel={() => {
                    setShowConfirmDialog(false);
                    setPreviewData(null);
                }}
                className="max-w-xl"
            >
                {previewData && (
                    <div className='p-6 space-y-6'>
                        <h3 className='text-2xl font-bold text-gray-900'>
                            {getPreviewHeadline(previewData.action)}
                        </h3>
                        
                        <div className='p-5 bg-gray-50 rounded-2xl space-y-3'>
                            {/* 현재 → 새 요금제 */}
                            <div className='flex items-center justify-between'>
                                <span className='text-base text-gray-600'>요금제</span>
                                <span className='text-base font-medium text-gray-900'>
                                    {previewData.currentPlan === 'standard' ? '스탠다드' : '플러스'} → {previewData.newPlan === 'standard' ? '스탠다드' : '플러스'}
                                </span>
                            </div>
                            
                            {/* 사용일수/남은일수 */}
                            <div className='flex items-center justify-between'>
                                <span className='text-base text-gray-600'>사용일수</span>
                                <span className='text-base font-medium text-gray-900'>
                                    {previewData.daysUsed}일 / 남은 기간 {previewData.remainingDays}일
                                </span>
                            </div>
                            
                            {/* 일할 계산 */}
                            <div className='pt-3 border-t border-gray-200'>
                                <div className='flex items-center justify-between mb-2'>
                                    <span className='text-sm text-gray-500'>기존 플랜 남은 금액</span>
                                    <span className='text-sm text-gray-700'>{formatNumber(previewData.remainingOldAmount)}원</span>
                                </div>
                                <div className='flex items-center justify-between'>
                                    <span className='text-sm text-gray-500'>새 플랜 남은 금액</span>
                                    <span className='text-sm text-gray-700'>{formatNumber(previewData.remainingNewAmount)}원</span>
                                </div>
                            </div>
                        </div>

                        {/* 차액 결과 */}
                        {previewData.action === 'charge' && (
                            <div className='p-5 bg-yellow-50 rounded-2xl border-2 border-yellow-200'>
                                <p className='text-sm text-yellow-800 mb-3'>
                                    차액 결제 내역이며 {formatNumber(previewData.difference)}원이 진행됩니다.
                                </p>
                                <div className='flex items-center justify-between'>
                                    <span className='text-lg font-bold text-yellow-900'>추가 결제 금액</span>
                                    <span className='text-2xl font-bold text-yellow-900'>{formatNumber(previewData.difference)}원</span>
                                </div>
                            </div>
                        )}
                        {previewData.action === 'refund' && (
                            <div className='p-5 bg-green-50 rounded-2xl border-2 border-green-200'>
                                <p className='text-sm text-green-800 mb-3'>
                                    차액 환불 내역이며 {formatNumber(previewData.refundAmount ?? previewData.difference)}원이 PG를 통해 반환됩니다.
                                </p>
                                <div className='flex items-center justify-between'>
                                    <span className='text-lg font-bold text-green-900'>환불 예정 금액</span>
                                    <span className='text-2xl font-bold text-green-900'>{formatNumber(previewData.refundAmount ?? previewData.difference)}원</span>
                                </div>
                            </div>
                        )}
                        
                        {previewData.action === 'refund' && (
                            <div className='p-5 bg-blue-50 rounded-2xl border-2 border-blue-200'>
                                <p className='text-sm text-blue-800 mb-3'>
                                    차액 결제 내역이며 {formatNumber(previewData.refundAmount)}원이 진행됩니다.
                                </p>
                                <div className='flex items-center justify-between'>
                                    <span className='text-lg font-bold text-blue-900'>환불 금액</span>
                                    <span className='text-2xl font-bold text-blue-900'>{formatNumber(previewData.refundAmount)}원</span>
                                </div>
                            </div>
                        )}

                        {previewData.action === 'none' && (
                            <div className='p-5 bg-gray-50 rounded-2xl border-2 border-gray-200'>
                                <p className='text-base text-gray-700 text-center'>차액이 없어 추가 결제나 환불이 발생하지 않습니다.</p>
                            </div>
                        )}

                        <p className='text-sm text-gray-500 text-center'>
                            요금제를 변경하시겠습니까?
                        </p>
                    </div>
                )}
            </Dialog>

            {/* 유료결제 서비스 약관 모달 */}
            <Dialog
                open={showTos}
                okText="확인"
                cancelText="닫기"
                onOk={() => setShowTos(false)}
                onCancel={() => setShowTos(false)}
                className="max-w-3xl"
            >
                <div className='p-5 space-y-4 max-h-[60vh] overflow-auto'>
                    <h3 className='text-xl font-bold text-gray-900'>유료결제 서비스 약관</h3>
                    <pre className='whitespace-pre-wrap text-sm text-gray-700'>
{`<유료결제 서비스 약관>

제1조 (목적)

본 약관은 (주)알파영어(이하 "회사")가 제공하는 온라인 영어학습 시스템(이하 "서비스")의 이용과
관련하여,
이용요금, 결제방식, 환불 및 요금 변동 등 제반 사항을 정함을 목적으로 합니다.

제2조 (용어의 정의)

이 약관에서 사용하는 주요 용어의 정의는 다음과 같습니다.

1. 회사: '(주)알파영어'로, 본 서비스를 개발 및 운영하는 주체를 말합니다.

2. 서비스: 회사가 제공하는 영어학습 플랫폼 'alphaca.co.kr'을 포함한 웹/모바일 기반 학습 시스템
일체를 의미합니다.

3. 학원: 회사와 서비스 이용계약을 체결한 사업자 단위의 고객으로, 서비스에 등록된 관리 권한을
가진 주체를 말합니다.

4. 강사: 학원에 소속되어 학습 관리를 수행하는 사용자로, 회사의 승인을 거쳐 관리자 또는 보조
관리자 권한을 가진 자를 말합니다.

5. 학생: 학원에 소속된 학습자 계정으로, 실질적으로 서비스를 통해 학습을 수행하는 사용자를 말
합니다.

6. 요금제: 학원이 사용하고자 선택한 사용 학생 수, 기능, 제공 콘텐츠 등을 합산하여 회사가 학원
에게 제공하는 상품을 말합니다.

7. 결제주기: 정기결제 최초 결제일을 기준으로, 만 30일을 1주기로 하며 매월 결제일은 변동될 수
있습니다.

8. 개별상품: 서비스 내에서 정기결제 상품이 제공하지 않는 콘텐츠를 활용하기 위해 개별적으로
결제하는 상품 일체를 의미합니다.

제3조 (이용요금 및 과금 기준)

① 서비스는 학원 단위로 과금되며, 학원에 등록된 학생 수와 제공 기능의 가격을 합산하여 이를
기준으로 월 사용료가 산정됩니다.

② 월 사용료는 결제 화면에 표시된 금액을 따릅니다. 단, 월 사용료 인상 등의 변화가 있을 경우,
변경 14일 이전에 사용자에게 고지합니다.

③ 사용 도중 학원은 언제든지 사용하고자 하는 요금제를 관리자 페이지 등을 통해 변경할 수 있으
며, 변경 시 차액이 발생할 경우 추가 결제 또는 환불이 진행될 수 있습니다.

단, 요금제 변경 시점에 따라 기능 차단이나 추가가 실시간 반영되지 않을 수 있으며, 이로 인한 일
시적 서비스 차이는 환불 사유에 포함되지 않습니다.

④ 요금제 변경은 월 1회로 제한됩니다.

제4조 (결제 방식 및 주기)

① 정기결제는 선불제 방식이며, 만 30일을 기준으로 자동 청구됩니다.

② 결제 수단은 신용카드, 계좌이체 등 회사가 지정한 방식 중 선택할 수 있습니다.

③ 학원은 관리자 페이지를 통해 결제 내역, 요금 기준, 학생 수, 요금제 변경 시 예상 금액 등을 열
람할 수 있습니다.

제5조 (결제 실패 및 유예 기간)

① 결제가 실패할 경우, 회사는 최초 실패일로부터 7일간 유예 기간을 학원에게 부여합니다.

② 유예 기간 내 결제가 완료되지 않으면 서비스는 자동 중지되며, 학원 관리자에게 서비스 화면상
에 표시되는 UI를 표시합니다.

또한, 유선, SMS, 카카오톡, 이메일 등을 통해 해당 사실을 통보합니다. 이러한 통보에도 불구하고 
미납으로 인한 서비스 중단, 데이터 파기 등의 불이익은 학원의 책임입니다.

③ 유예 기간 내에는 서비스 이용에 제한이 없습니다. 단, 유예 기간이 초과될 경우 회사는 학원의
서비스를 일시 중단합니다.

④ 일시 중단 기간은 유예 기간 초과 후 7일간이며, 이 기간 내에 학원은 청구 금액을 결제하여 서
비스를 재개하거나, 서비스 해지를 선택할 수 있습니다.

제6조 (요금 정책의 변경 및 고지)

① 회사는 운영상 필요에 따라 요금제 및 과금 구조를 변경할 수 있습니다. 월 사용료 및 개별상품
가격에 변동이 있을 경우, 회사는 최소 14일 전 결제 주체인 학원에게 고지하여야 합니다.

② 변동 사항 적용 이전에 구매한 내역에는 영향을 미치지 않으며, 변경된 요금제 및 과금 구조에
동의하지 않을 경우, 학원은 서면, 유선 등의 수단을 통해 회사에 해지 의사를 표시하거나, 마이페
이지 등을 통해 직접 해지 신청할 수 있습니다. 해지 시 본 약관 제7조를 따릅니다.

③ 학원의 희망에 따라 등록 학생 수 또는 사용 기능 등 요금제 금액 변경을 요청할 수 있으며, 합
리적인 사유가 없는 한 회사는 이를 자동 수락합니다.

④ 요금제 변경에 따른 추가 결제 또는 환불은 다음 산식에 따라 계산되며, 소수 첫째 자리에서 반
올림 처리합니다.

(기존 결제금액 × 잔여일수 ÷ 30) (변경예정금액 × 잔여일수 ÷ 30) = 환불 또는 추가 금액 −

예시: 총 30,000원 요금제를 5일 경과 후(잔여 25일) 20,000원 요금제로 변경 시

→ (30,000 × 25 ÷ 30) (20,000 × 25 ÷ 30) = 8,334원 환불 −

⑤ 회사가 진행하는 프로모션 또는 행사에 따른 일시적 가격 변동은 사용자에게 고지할 의무가 없
습니다.

⑥ 사용자가 해지 의사를 표시하지 않고 결제를 지속할 경우, 회사의 변경된 요금제에 동의한 것으
로 간주합니다.

제7조 (청약철회와 서비스 해지 및 환불)

① 학원이 서비스 해지를 희망할 경우, 합리적인 사유가 없는 한 회사는 이를 수락하여야 합니다.

② 서비스 해지 신청이 완료된 경우, 회사는 남은 잔여일 수만큼 서비스를 제공하며, 잔여일에 대한
환불은 불가합니다.

③ 개별상품은 구매 후 7일 이내 환불이 가능하며, 사용을 시작한 경우에는 환불이 불가합니다.

④ 월 사용료는 원칙적으로 환불되지 않으며, 다음 각 호에 해당할 경우 예외적으로 환불이 가능합
니다.

1. 월 사용료 결제 후 7일 이내이며 사용 이력이 전혀 없는 경우

2. 서비스 장애로 72시간 이상 연속 이용 불가 시: 장애 기간 일수만큼 일할 계산하여 환불

⑤ 환불은 학원 명의 또는 학원 대표자 명의 계좌로만 가능하며, 요청일로부터 영업일 기준 7일 이
내에 처리됩니다.

⑥ 카드로 결제된 건은 해당 카드사를 통해 결제 취소 처리됩니다.

⑦ 본 약관에서 정하지 않은 환불 기준 및 분쟁 발생 시, 「소비자분쟁해결기준(공정거래위원회 고
시)」을 준용합니다.

제8조 (데이터 보관 및 삭제)

① 서비스 이용 중지 또는 해지 시, 학원 계정에 속한 학생의 학습 이력 및 성적 등 데이터는 해지
일로부터 30일간 보관됩니다.

② 보관 기간이 경과하면 해당 데이터는 자동 삭제되며, 복구가 불가능합니다.

③ 서비스 이용중, 혹은 보관기간 내에도 학원이 회사에게 데이터 삭제를 요청할 수 있으며, 삭제
시 복구는 불가합니다.

④ 데이터 보관 기간 경과 후 삭제된 정보에 대해서는 회사가 어떠한 법적/기술적 책임도 지지 않
습니다.

⑤ 해지 이전에 학원은 관리자 페이지를 통해 성적표, 출결 이력, 과제 결과 등의 데이터를 직접 백
업할 수 있습니다.

단, 회사는 특별한 사유(과실, 책임 등)가 없다면 학원의 백업을 대행해드리지 않습니다.

⑥ 단, 전자상거래 등에서의 소비자보호에 관한 법률, 부가가치세법 등 관계 법령에 따라 아래 각
항목은 다음의 기간 동안 별도로 보관됩니다.

1. 계약 또는 청약철회 등에 관한 기록: 5년 보관 (전자상거래법 제6조 및 동 시행령 제6조)

2. 대금결제 및 재화 등의 공급에 관한 기록: 5년 보관 (전자상거래법)

3. 소비자의 불만 또는 분쟁처리에 관한 기록: 3년 보관 (전자상거래법)

4. 전자금융 거래에 관한 기록: 5년 보관 (전자금융거래법 제22조)

5. 세금계산서, 영수증 등 회계 및 세무 관련 서류: 5년 보관 (부가가치세법, 국세기본법)

6. 서비스 접속기록(IP 등): 3개월 보관 (통신비밀보호법 제15조의2)

위 보관 기간이 경과한 후에는 관련 법령에 따라 안전하게 파기됩니다.

제9조 (기타)

① 본 약관에 명시되지 않은 사항은 전자상거래법, 콘텐츠산업진흥법, 민법 등 관계 법령 및 개별
계약서에 따릅니다.

② 본 약관은 학원 관리자에게 충분히 고지된 것으로 간주되며, 회사는 관련 기록을 보관할 의무를
집니다.

③ 본 약관은 회사의 정책에 따라 변경될 수 있으며, 변경 시에는 제6조의 고지 절차를 준용합니다.

④ 회사는 계정 공유, 비정상적인 로그인 시도, 기타 부정 사용을 방지하기 위해 서비스 이용 내역
을 모니터링할 수 있으며,

이러한 행위가 확인될 경우 사전 통보 없이 서비스 이용을 제한하거나, 계약 해지 등의 조치를 취
할 수 있습니다.

부칙

본 약관은 2026년 1월 1일 부터 시행되며, 시행 현재 개정이력은 없습니다.`}
                    </pre>
                </div>
            </Dialog>
        </main>
    )
}